/**
 * QuickBundlesManager - Shared component for managing Quick Order bundles
 * 
 * Used in ProductConfigFlowsPage to provide full CRUD functionality
 * with consistent styling and all visibility/pricing features.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus,
  Package,
  Edit,
  Trash2,
  Save,
  X,
  Crown,
  Loader2,
  Eye,
  EyeOff,
  DollarSign,
  Layers,
  Globe,
  Building2,
  Megaphone,
  Search,
  Check,
  Sliders,
  Copy,
  FileImage
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import FamilyScopePicker, { CrossFamilyWarning } from '../FamilyScopePicker';

import { API_BASE } from '../../lib/apiBase';
const API_URL = API_BASE;
const getToken = () => localStorage.getItem('token');

// Tier configuration
const TIER_CONFIG = {
  budget: { label: 'Budget', color: 'bg-slate-100 text-slate-700', icon: null },
  standard: { label: 'Standard', color: 'bg-emerald-100 text-emerald-700', icon: null },
  premium: { label: 'Premium', color: 'bg-amber-100 text-amber-700', icon: Crown }
};

// Multi-Company Selector Component
function CompanyMultiSelect({ selectedIds, onChange, companies, onSearch }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  
  const handleSearch = (q) => {
    setSearchQuery(q);
    onSearch(q);
  };
  
  const filteredCompanies = companies.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const toggleCompany = (companyId) => {
    if (selectedIds.includes(companyId)) {
      onChange(selectedIds.filter(id => id !== companyId));
    } else {
      onChange([...selectedIds, companyId]);
    }
  };
  
  const selectedCompanies = companies.filter(c => selectedIds.includes(c.id));
  
  return (
    <div className="relative">
      <div 
        className="flex items-center justify-between w-full min-h-[40px] px-3 py-2 border rounded-md cursor-pointer hover:border-gray-400"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {selectedCompanies.length === 0 ? (
            <span className="text-slate-400">Select companies...</span>
          ) : (
            selectedCompanies.map(c => (
              <Badge key={c.id} variant="secondary" className="text-xs">
                {c.name}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCompany(c.id);
                  }}
                  className="ml-1 hover:text-red-500"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))
          )}
        </div>
        <Search className="h-4 w-4 text-slate-400 ml-2 flex-shrink-0" />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b">
            <Input
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-8"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredCompanies.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 text-center">
                No companies found
              </div>
            ) : (
              filteredCompanies.map(company => (
                <div
                  key={company.id}
                  className={`px-3 py-2 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                    selectedIds.includes(company.id) ? 'bg-indigo-50' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCompany(company.id);
                  }}
                >
                  <span className={selectedIds.includes(company.id) ? 'text-indigo-700 font-medium' : ''}>
                    {company.name}
                  </span>
                  {selectedIds.includes(company.id) && (
                    <Check className="h-4 w-4 text-indigo-600" />
                  )}
                </div>
              ))
            )}
          </div>
          {selectedIds.length > 0 && (
            <div className="p-2 border-t bg-slate-50">
              <button
                className="text-xs text-slate-500 hover:text-slate-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * BundledSpecsSection — admin editor for `bundle.bundled_options`.
 *
 * Renders a family-aware set of pre-filled spec inputs (weight, color,
 * speed, format, etc.). Each input writes to / reads from the parent
 * `bundled_options` object via `onChange(next)`. Empty values are stripped
 * so the resulting dict only contains keys the admin explicitly set —
 * downstream consumers fall back to their own defaults / SKU heuristics
 * for unset keys, preserving backwards compatibility.
 */
function BundledSpecsSection({ family, value, onChange }) {
  const opts = value || {};

  // Helper: write key (strip empty / null so the saved object is minimal).
  const writeKey = (key, next) => {
    const merged = { ...opts };
    if (next === '' || next === null || next === undefined) {
      delete merged[key];
    } else {
      merged[key] = next;
    }
    onChange(merged);
  };

  const clearAll = () => onChange({});

  // Family-specific spec definitions. Each entry is rendered as a Select
  // (or Input when `type='number'`). The `key` matches what the Quick
  // Order wizard / Campaign flow / submit handler will read from
  // `bundle.bundled_options`.
  // Weight options depend on the selected format — matches the pricing
  // system + product configuration (avoids invalid bundle configs like
  // "7\" @ 180g" which doesn't exist).
  const VINYL_WEIGHT_BY_FORMAT = {
    '7"': [36],
    '10"': [110],
    '12"': [140, 180],
  };

  const SPEC_DEFS = {
    vinyl: [
      { key: 'format', label: 'Format', options: ['7"', '10"', '12"'] },
      {
        key: 'vinyl_weight_g',
        label: 'Weight',
        type: 'number',
        // Resolved at render time from the currently-selected format.
        // `options_by(opts)` returns the valid list for the format the
        // admin picked; if no format is picked yet, expose EVERY valid
        // weight so the field remains editable without forcing an order.
        options_by: (o) => VINYL_WEIGHT_BY_FORMAT[o?.format] || [36, 110, 140, 180],
        suffix: 'g',
      },
      { key: 'speed', label: 'Speed (RPM)', options: ['33 RPM', '45 RPM'] },
      { key: 'color_mode', label: 'Color mode', options: ['black', 'color'] },
      { key: 'test_pressings', label: 'Test pressings', type: 'number', options: [0, 5, 10] }
    ],
    cd: [
      { key: 'format', label: 'Format', options: ['CD', 'CD-R'] },
      { key: 'jacket_style', label: 'Packaging', options: ['Jewel Case', 'Digipak', 'Cardboard Sleeve'] }
    ],
    dvd: [
      { key: 'format', label: 'Format', options: ['DVD-5', 'DVD-9'] },
      { key: 'jacket_style', label: 'Packaging', options: ['Amaray', 'Digipak'] }
    ],
    bluray: [
      { key: 'format', label: 'Format', options: ['BD-25', 'BD-50'] },
      { key: 'jacket_style', label: 'Packaging', options: ['Amaray', 'Digipak', 'Steelbook'] }
    ]
  };

  const defs = SPEC_DEFS[family] || SPEC_DEFS.vinyl;
  const hasAny = Object.keys(opts).length > 0;

  return (
    <div className="space-y-4 border-t pt-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-slate-700 flex items-center gap-2">
          <Sliders className="h-4 w-4" />
          Pre-filled Specs
        </h3>
        {hasAny && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="bundled-specs-clear"
          >
            Clear all
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 -mt-2">
        Pre-fills product configuration when this bundle is selected. Leave a
        field blank to let the customer / system default apply. Replaces the
        legacy SKU/title text matching for weight, format, etc.
      </p>

      <div className="grid grid-cols-2 gap-4">
        {defs.map((def) => {
          const current = opts[def.key] ?? '';
          // Resolve options: static array on the def OR a function that
          // takes the current opts (used for format-dependent weights).
          const resolvedOptions = typeof def.options_by === 'function'
            ? def.options_by(opts)
            : def.options;
          // If the currently-stored value is no longer valid for the new
          // context (e.g. weight=180 was chosen for 12" then admin
          // switched to 7"), clear it silently so the Select doesn't
          // render a stale/invalid selected item.
          const currentInOptions = current === '' ||
            resolvedOptions.some((o) => String(o) === String(current));
          const effectiveCurrent = currentInOptions ? current : '';
          if (!currentInOptions && current !== '') {
            // Fire-and-forget clear on the next tick — avoids the
            // "setState during render" React warning.
            setTimeout(() => writeKey(def.key, ''), 0);
          }
          return (
            <div key={def.key} className="space-y-2">
              <Label className="text-xs">{def.label}</Label>
              <Select
                value={effectiveCurrent === '' ? '__unset__' : String(effectiveCurrent)}
                onValueChange={(v) => {
                  if (v === '__unset__') {
                    writeKey(def.key, '');
                  } else if (def.type === 'number') {
                    writeKey(def.key, parseInt(v, 10));
                  } else {
                    writeKey(def.key, v);
                  }
                }}
              >
                <SelectTrigger data-testid={`bundled-spec-${def.key}`}>
                  <SelectValue placeholder="— not set —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unset__">— not set —</SelectItem>
                  {resolvedOptions.map((o) => (
                    <SelectItem key={String(o)} value={String(o)}>
                      {def.suffix ? `${o}${def.suffix}` : String(o)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}



// Shared helper: compute a bundle's effective Quote Required state by
// evaluating every Sales SKU actually included in it (base SKU + its
// linked setup fees + per-unit add-ons when the pricing strategy
// requires them). Pure read-only — never writes to the DB and never
// mutates ``sales_rate_items.requires_quote`` or any BOM-level flag.
//
// Returns::
//
//     {
//       manual: bool,               // ANY included SKU has requires_quote=true
//       bom: bool,                  // ANY included SKU's BOM has is_quotable=false leaves
//       state: "none" | "manual" | "bom" | "both",
//       triggering_manual_skus: [str, ...],
//       bom_components: [{supplier_sku, procurement_item_name, from_sku}, ...]
//     }
//
// ``bomQuoteGateByProductId`` is the cache populated by the parent
// component from ``POST /api/bom/quote-check``. Nested BOMs and
// applicability filters are therefore honoured implicitly because the
// server-side helper (``services/quote_gate.py``) already handles them.
function computeBundleQuoteState({
  baseSalesSku,
  pricingStrategy,
  rateCardItems,
  bomQuoteGateByProductId,
}) {
  const result = {
    manual: false,
    bom: false,
    state: 'none',
    triggering_manual_skus: [],
    bom_components: [],
  };
  if (!baseSalesSku || !Array.isArray(rateCardItems)) return result;

  const baseItem = rateCardItems.find((i) => i && i.sales_sku === baseSalesSku);
  if (!baseItem) return result;

  // Build the full list of included SKUs. For ``sku_only`` strategy only
  // the base SKU participates; for ``sku_with_linked_components`` we
  // also walk setup_fee_skus + per_unit_addon_skus (same expansion the
  // Sales pricing engine performs at order time).
  const includedSkus = [baseSalesSku];
  if (pricingStrategy === 'sku_with_linked_components') {
    const pushEntry = (entry) => {
      if (!entry) return;
      const sku = typeof entry === 'string' ? entry : entry.sku;
      if (sku && !includedSkus.includes(sku)) includedSkus.push(sku);
    };
    (baseItem.setup_fee_skus || []).forEach(pushEntry);
    (baseItem.per_unit_addon_skus || []).forEach(pushEntry);
  }

  const seenBoms = new Set();
  for (const sku of includedSkus) {
    const item = rateCardItems.find((i) => i && i.sales_sku === sku);
    if (!item) continue;
    if (item.requires_quote) {
      result.manual = true;
      if (!result.triggering_manual_skus.includes(sku)) {
        result.triggering_manual_skus.push(sku);
      }
    }
    if (item.generate_purchase_line !== false && item.bom_product_id) {
      const gate = bomQuoteGateByProductId[item.bom_product_id];
      if (gate && gate.quote_required && !seenBoms.has(item.bom_product_id)) {
        seenBoms.add(item.bom_product_id);
        result.bom = true;
        for (const c of (gate.components || [])) {
          result.bom_components.push({
            supplier_sku: c.supplier_sku,
            procurement_item_name: c.procurement_item_name,
            from_sku: sku,
          });
        }
      }
    }
  }

  if (result.manual && result.bom) result.state = 'both';
  else if (result.manual) result.state = 'manual';
  else if (result.bom) result.state = 'bom';
  else result.state = 'none';
  return result;
}


// Bundle Editor Modal
function BundleEditorModal({ bundle, rateCardItems: parentRateCardItems, companies, activeFamily = 'vinyl', bomQuoteGateByProductId = {}, onSave, onClose, onSearchCompanies }) {
  const [formData, setFormData] = useState({
    title: bundle?.title || '',
    subtitle: bundle?.subtitle || '',
    description: bundle?.description || '',
    tier: bundle?.tier || 'standard',
    format_label: bundle?.format_label || '',
    base_sales_sku: bundle?.base_sales_sku || '',
    // Phase 2: new bundles default to the active tab's family. Editing
    // preserves the existing bundle's family.
    product_family: bundle?.product_family || activeFamily || 'vinyl',
    pricing_strategy: (bundle?.pricing_strategy === 'component_based'
      ? 'sku_with_linked_components'
      : bundle?.pricing_strategy) || 'sku_only',
    allowed_quantity_choices: bundle?.allowed_quantity_choices || [100, 250, 500, 1000],
    default_quantity: bundle?.default_quantity || 500,
    display_order: bundle?.display_order || 0,
    is_active: bundle?.is_active ?? true,
    is_global: bundle?.is_global ?? true,
    company_ids: bundle?.company_ids || [],
    is_campaign_only: bundle?.is_campaign_only ?? false,
    // Pre-filled spec overrides merged into vinyl_specs / cd_specs etc when
    // the bundle is selected. Replaces the legacy SKU/title text matching
    // for weight, format, color, etc. Keys are free-form; the consumer
    // (Quick Order wizard, Campaign flow) decides which it cares about.
    bundled_options: bundle?.bundled_options || {},
    image_url: bundle?.image_url || ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [quantityInput, setQuantityInput] = useState('');
  // Cascade confirmation state. When bundled_options is dirty against the
  // saved bundle AND there are eligible existing orders, we ask the admin
  // whether to also push the new spec values to those orders before saving.
  const [cascadePrompt, setCascadePrompt] = useState(null);
  // ^ { affected_count, excluded_count, affected_orders } | null
  // Phase 2 (soft scoping): independent scope for the Base Product SKU
  // selector. Default 'current' on create; 'all' on edit so any pre-existing
  // cross-family selection is visible.
  const [skuScope, setSkuScope] = useState(bundle?.id ? 'all' : 'current');
  const [rateCardItems, setRateCardItems] = useState(parentRateCardItems || []);

  // Modal-local BOM quote-gate cache. Mirrors the parent cache in
  // ``QuickBundlesManager`` but keyed off the modal's own
  // ``rateCardItems`` (which may include cross-family items when the
  // admin widens the SKU pool scope). The two caches are merged at
  // read time; identical lookups just resolve to the same value.
  const [localBomQuoteGate, setLocalBomQuoteGate] = useState({});
  useEffect(() => {
    const needed = Array.from(
      new Set(
        (rateCardItems || [])
          .map((it) => (it && it.generate_purchase_line !== false ? it.bom_product_id : null))
          .filter(Boolean)
      )
    ).filter((pid) => !(pid in bomQuoteGateByProductId));
    if (needed.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          needed.map(async (pid) => {
            try {
              const res = await fetch(`${API_URL}/api/bom/quote-check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                body: JSON.stringify({ product_id: pid }),
              });
              if (!res.ok) return [pid, { quote_required: false, count: 0, components: [] }];
              const body = await res.json();
              return [pid, {
                quote_required: !!body.quote_required,
                count: body.count || 0,
                components: body.components || [],
              }];
            } catch {
              return [pid, { quote_required: false, count: 0, components: [] }];
            }
          })
        );
        if (cancelled) return;
        const merge = {};
        for (const [pid, gate] of results) merge[pid] = gate;
        setLocalBomQuoteGate((prev) => ({ ...prev, ...merge }));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [rateCardItems, bomQuoteGateByProductId]);
  // Combined view used by ``computeBundleQuoteState`` below.
  const mergedBomQuoteGate = useMemo(
    () => ({ ...localBomQuoteGate, ...bomQuoteGateByProductId }),
    [localBomQuoteGate, bomQuoteGateByProductId]
  );

  // Refetch when the bundle's family or scope changes — uses the enriched
  // cross-card endpoint so each item carries source_rate_card_name +
  // source_rate_card_product_family for badge rendering.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const resolved =
          skuScope === 'current' ? (formData.product_family || 'vinyl') : skuScope;
        const res = await fetch(
          `${API_URL}/api/sales/rate-items/cross-card?product_family=${encodeURIComponent(resolved)}`,
          { headers: { 'Authorization': `Bearer ${getToken()}` } }
        );
        if (!cancelled && res.ok) {
          const data = await res.json();
          setRateCardItems(data || []);
        }
      } catch (err) {
        console.error('Failed to fetch SKUs for bundle modal scope:', err);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.product_family, skuScope]);

  const isNew = !bundle?.id;
  
  const itemsByCategory = rateCardItems.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Look up the selected SKU's family for the cross-family warning chip.
  const selectedSkuItem = rateCardItems.find(i => i.sales_sku === formData.base_sales_sku);
  const isCrossFamilySku =
    selectedSkuItem &&
    (selectedSkuItem.source_rate_card_product_family || 'vinyl') !==
      (formData.product_family || 'vinyl');

  // Deep-equal check for bundled_options vs the persisted bundle. We don't
  // pull in lodash for one comparison — JSON.stringify on the two dicts is
  // fine here since values are scalars/strings.
  const bundledOptionsDirty = (() => {
    if (!bundle?.id) return false;  // new bundle → no cascade target
    const before = JSON.stringify(bundle?.bundled_options || {});
    const after = JSON.stringify(formData.bundled_options || {});
    return before !== after;
  })();

  const performSave = async ({ cascadeBundledOptions } = {}) => {
    setSaving(true);
    try {
      await onSave(formData, { cascadeBundledOptions });
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save bundle');
    } finally {
      setSaving(false);
      setCascadePrompt(null);
    }
  };

  const handleSave = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    if (!formData.base_sales_sku) {
      toast.error('Base SKU is required for pricing');
      return;
    }

    // When bundled_options changed AND this is an existing bundle, ask the
    // backend how many live orders would be affected by a cascade. We only
    // show the prompt when count > 0 — otherwise just save silently.
    if (bundledOptionsDirty && bundle?.id) {
      try {
        const res = await fetch(
          `${API_URL}/api/product-config/admin/bundles/${bundle.id}/cascade-preview`,
          { headers: { 'Authorization': `Bearer ${getToken()}` } }
        );
        if (res.ok) {
          const preview = await res.json();
          if ((preview.affected_count || 0) > 0) {
            setCascadePrompt(preview);
            return;  // wait for user to choose in the dialog
          }
        }
      } catch (err) {
        console.warn('[Cascade preview] failed; falling back to no-cascade save:', err);
      }
    }

    await performSave({ cascadeBundledOptions: false });
  };

  const resolveImageUrl = (u) => {
    if (!u) return '';
    return /^https?:\/\//.test(u) || u.startsWith('data:') ? u : `${API_URL}${u}`;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/product-config/admin/bundle-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setFormData(prev => ({ ...prev, image_url: data.image_url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleAddQuantity = () => {
    const qty = parseInt(quantityInput);
    if (qty > 0 && !formData.allowed_quantity_choices.includes(qty)) {
      setFormData(prev => ({
        ...prev,
        allowed_quantity_choices: [...prev.allowed_quantity_choices, qty].sort((a, b) => a - b)
      }));
      setQuantityInput('');
    }
  };

  const handleRemoveQuantity = (qty) => {
    setFormData(prev => ({
      ...prev,
      allowed_quantity_choices: prev.allowed_quantity_choices.filter(q => q !== qty)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {isNew ? 'Create Bundle' : 'Edit Bundle'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder='12" LP — Standard'
                  data-testid="bundle-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Format Label</Label>
                <Input
                  value={formData.format_label}
                  onChange={(e) => setFormData(prev => ({ ...prev, format_label: e.target.value }))}
                  placeholder='12" LP'
                  data-testid="bundle-format-label"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Input
                value={formData.subtitle}
                onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                placeholder="Professional quality release"
                data-testid="bundle-subtitle"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed description of what's included..."
                className="w-full px-3 py-2 border rounded-md text-sm min-h-[80px]"
                data-testid="bundle-description"
              />
            </div>

            {/* Bundle image — shown at the top of the Quick Order card. */}
            <div className="space-y-2">
              <Label>Bundle Image</Label>
              <div className="flex items-start gap-4">
                <div className="h-24 w-32 flex-shrink-0 rounded-lg border bg-slate-50 overflow-hidden flex items-center justify-center">
                  {formData.image_url ? (
                    <img
                      src={resolveImageUrl(formData.image_url)}
                      alt="Bundle"
                      className="h-full w-full object-cover"
                      data-testid="bundle-image-preview"
                    />
                  ) : (
                    <FileImage className="h-7 w-7 text-slate-300" data-testid="bundle-image-placeholder" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      data-testid="bundle-image-input"
                    />
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border bg-white hover:bg-slate-50 cursor-pointer">
                      {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileImage className="h-4 w-4" />}
                      {uploadingImage ? 'Uploading…' : (formData.image_url ? 'Replace image' : 'Upload image')}
                    </span>
                  </label>
                  {formData.image_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                      className="text-slate-500 hover:text-red-600 justify-start px-2 h-8"
                      data-testid="bundle-image-remove"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  )}
                  <p className="text-[11px] text-slate-400 max-w-[220px]">JPEG, PNG or WEBP, up to 8MB. Shown on the Quick Order card.</p>
                </div>
              </div>
            </div>

            {/* Phase 2: Product Family — defaults to active tab, can be
                changed in-modal. The base SKU pool refetches automatically
                when this changes (see useEffect above). */}
            <div className="space-y-2">
              <Label>Product Family *</Label>
              <Select
                value={formData.product_family}
                onValueChange={(v) => setFormData(prev => ({ ...prev, product_family: v }))}
              >
                <SelectTrigger data-testid="bundle-product-family">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vinyl">Vinyl</SelectItem>
                  <SelectItem value="cd">CD</SelectItem>
                  <SelectItem value="dvd">DVD</SelectItem>
                  <SelectItem value="bluray">Blu-ray</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">
                Customers only see this bundle when ordering for the same family. Admin SKU selection below can still pull from other families if you choose.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tier</Label>
                <Select 
                  value={formData.tier} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tier: v }))}
                >
                  <SelectTrigger data-testid="bundle-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="budget">Budget</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  data-testid="bundle-display-order"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
                    data-testid="bundle-active-toggle"
                  />
                  <span className="text-sm text-slate-600">
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quantity Options */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Quantity Options
            </h3>
            
            <div className="flex flex-wrap gap-2">
              {formData.allowed_quantity_choices.map(qty => (
                <Badge 
                  key={qty} 
                  variant="secondary" 
                  className="px-3 py-1 flex items-center gap-1"
                >
                  {qty}
                  <button 
                    onClick={() => handleRemoveQuantity(qty)}
                    className="ml-1 hover:text-red-500"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={quantityInput}
                onChange={(e) => setQuantityInput(e.target.value)}
                placeholder="Add quantity..."
                className="w-32"
                onKeyDown={(e) => e.key === 'Enter' && handleAddQuantity()}
              />
              <Button variant="outline" size="sm" onClick={handleAddQuantity}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Default Quantity</Label>
              <Select 
                value={String(formData.default_quantity)} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, default_quantity: parseInt(v) }))}
              >
                <SelectTrigger className="w-32" data-testid="bundle-default-qty">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {formData.allowed_quantity_choices.map(qty => (
                    <SelectItem key={qty} value={String(qty)}>{qty}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pre-filled Specs (bundled_options) — pre-populates product
              config when the bundle is selected. The Quick Order wizard,
              Campaign flow and order-submit all consume the same keys, so
              configuring once here cascades everywhere. Family-aware:
              Vinyl shows vinyl-specific keys, CD/DVD/Bluray show their own.
              Leave keys empty/unset to let the wizard's defaults apply. */}
          <BundledSpecsSection
            family={formData.product_family}
            value={formData.bundled_options}
            onChange={(next) => setFormData(prev => ({ ...prev, bundled_options: next }))}
          />

          {/* Pricing */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Pricing
            </h3>

            {/* Strategy selector */}
            <div className="space-y-2">
              <Label>Pricing Strategy</Label>
              <div className="grid grid-cols-2 gap-3">
                <label
                  className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData.pricing_strategy === 'sku_only'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="pricing_strategy"
                    value="sku_only"
                    checked={formData.pricing_strategy === 'sku_only'}
                    onChange={() => setFormData(prev => ({ ...prev, pricing_strategy: 'sku_only' }))}
                    data-testid="strategy-sku-only"
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-800">SKU only</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Price = Rate Card price for the selected base SKU × quantity.
                    </div>
                  </div>
                </label>
                <label
                  className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData.pricing_strategy === 'sku_with_linked_components'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="pricing_strategy"
                    value="sku_with_linked_components"
                    checked={formData.pricing_strategy === 'sku_with_linked_components'}
                    onChange={() => setFormData(prev => ({ ...prev, pricing_strategy: 'sku_with_linked_components' }))}
                    data-testid="strategy-sku-with-linked"
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-800">SKU with linked components</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Auto-includes setup fees + per-unit add-ons already linked to the base SKU.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Base Product SKU *</Label>
                <FamilyScopePicker
                  value={skuScope}
                  onChange={setSkuScope}
                  activeFamily={formData.product_family}
                  label="SKU pool"
                />
              </div>
              <Select 
                value={formData.base_sales_sku} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, base_sales_sku: v }))}
              >
                <SelectTrigger data-testid="bundle-base-sku">
                  <SelectValue placeholder="Select a product SKU..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {rateCardItems.length === 0 ? (
                    <div className="px-2 py-4 text-sm text-slate-500 text-center">
                      No SKUs match the current scope. Try widening "SKU pool" above or add items to a {formData.product_family.toUpperCase()} rate card.
                    </div>
                  ) : (
                    rateCardItems.map(item => {
                      const fam = (item.source_rate_card_product_family || 'vinyl').toUpperCase();
                      const src = item.source_rate_card_name ? ` — ${item.source_rate_card_name}` : '';
                      return (
                        <SelectItem key={item.sales_sku} value={item.sales_sku}>
                          {item.sales_sku} - {item.display_name || item.category} [{fam}]{src}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              {/* Cross-family warning chip — selection is allowed, just visible. */}
              {isCrossFamilySku && (
                <div className="flex items-center gap-1.5">
                  <CrossFamilyWarning
                    valueFamily={selectedSkuItem.source_rate_card_product_family || 'vinyl'}
                    activeFamily={formData.product_family}
                  />
                  <span className="text-[11px] text-amber-700">
                    Cross-family SKU — sourced from{' '}
                    <strong>{selectedSkuItem.source_rate_card_name}</strong> (
                    {(selectedSkuItem.source_rate_card_product_family || 'vinyl').toUpperCase()}).
                    Customers will still only see this bundle in the{' '}
                    {(formData.product_family || 'vinyl').toUpperCase()} order flow.
                  </span>
                </div>
              )}
              {/* Effective Quote Required status — recomputed live from the
                  CURRENT formData selection (base SKU + pricing strategy),
                  reusing the shared ``computeBundleQuoteState`` helper so
                  the badge can never disagree with the Sales pricing
                  engine. Informational only; saving is NEVER blocked. */}
              {formData.base_sales_sku && (() => {
                const q = computeBundleQuoteState({
                  baseSalesSku: formData.base_sales_sku,
                  pricingStrategy: formData.pricing_strategy,
                  rateCardItems,
                  bomQuoteGateByProductId: mergedBomQuoteGate,
                });
                const stateLabel = {
                  none: 'None',
                  manual: 'Manual only',
                  bom: 'Supplier BOM only',
                  both: 'Both (Manual + Supplier BOM)',
                }[q.state];
                const chipClass = q.state === 'none'
                  ? 'bg-white border-slate-300 text-slate-600'
                  : 'bg-amber-200 border-amber-400 text-amber-900';
                return (
                  <div
                    className={`mt-1 rounded-lg border p-3 space-y-1.5 ${q.state === 'none' ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50'}`}
                    data-testid="bundle-effective-quote-status-block"
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`font-medium ${q.state === 'none' ? 'text-slate-700' : 'text-amber-900'}`}>
                        Effective Quote Required:
                      </span>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 border rounded text-[11px] font-medium ${chipClass}`}
                        data-testid={`bundle-effective-quote-status-${q.state}`}
                      >
                        {stateLabel}
                      </span>
                    </div>
                    {q.manual && q.triggering_manual_skus.length > 0 && (
                      <div className="text-[11px] text-amber-800">
                        <span className="font-medium">Manual override:</span>{' '}
                        {q.triggering_manual_skus.slice(0, 3).join(', ')}
                        {q.triggering_manual_skus.length > 3 && ` and ${q.triggering_manual_skus.length - 3} more`}
                      </div>
                    )}
                    {q.bom && q.bom_components.length > 0 && (
                      <div className="text-[11px] text-amber-800">
                        <span className="font-medium">Supplier BOM:</span>{' '}
                        {q.bom_components.slice(0, 3).map((c, i) => (
                          <span key={`${c.from_sku}:${c.supplier_sku}:${i}`}>
                            {i > 0 ? ', ' : ''}
                            {c.procurement_item_name || c.supplier_sku}
                            <span className="text-amber-700"> ({c.from_sku})</span>
                          </span>
                        ))}
                        {q.bom_components.length > 3 && ` and ${q.bom_components.length - 3} more`}
                      </div>
                    )}
                    {q.state === 'none' && (
                      <div className="text-[11px] text-slate-500">
                        No manual override and no Supplier BOM items flagged as quote-required.
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500 pt-0.5">
                      Updates live as you change the base SKU, pricing strategy or linked components. Informational only — saving is not blocked.
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Preview linked components when strategy requires it */}
            {formData.pricing_strategy === 'sku_with_linked_components' && formData.base_sales_sku && (() => {
              const baseItem = rateCardItems.find(i => i.sales_sku === formData.base_sales_sku);
              if (!baseItem) {
                return (
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-xs text-amber-800">
                    Selected base SKU is not in the loaded rate-card list.
                  </div>
                );
              }
              const normalise = (e) => typeof e === 'string'
                ? { sku: e, applies_to: ['new_order'] }
                : { sku: e.sku, applies_to: e.applies_to || ['new_order'] };
              const setups = (baseItem.setup_fee_skus || []).map(normalise);
              const addons = (baseItem.per_unit_addon_skus || []).map(normalise);

              const badge = (applies_to) => {
                if (applies_to.length === 0)
                  return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-700">No rate on invoice</span>;
                return (
                  <span className="inline-flex gap-1">
                    {applies_to.includes('new_order') && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">New order</span>}
                    {applies_to.includes('reorder') && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700">Re-order</span>}
                  </span>
                );
              };

              if (setups.length === 0 && addons.length === 0) {
                return (
                  <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg text-xs text-indigo-700" data-testid="linked-preview-empty">
                    The base SKU has no linked setup fees or per-unit add-ons. Bundle will only include the base SKU.
                    <br />
                    Tip: Add linked components on the Rate Card item <strong>{formData.base_sales_sku}</strong> to auto-include them here.
                  </div>
                );
              }

              return (
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg space-y-3" data-testid="linked-preview">
                  <div className="text-xs font-medium text-slate-700">
                    Auto-included from base SKU's linked components:
                  </div>
                  {setups.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Setup fees</div>
                      <ul className="space-y-1">
                        {setups.map((s, idx) => (
                          <li key={`setup-${idx}`} className="flex items-center justify-between text-xs">
                            <span className="text-slate-700 font-mono">{s.sku}</span>
                            {badge(s.applies_to)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {addons.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Per-unit add-ons</div>
                      <ul className="space-y-1">
                        {addons.map((a, idx) => (
                          <li key={`addon-${idx}`} className="flex items-center justify-between text-xs">
                            <span className="text-slate-700 font-mono">{a.sku}</span>
                            {badge(a.applies_to)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Visibility */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Visibility
            </h3>

            {/* The three visibility modes are mutually exclusive. We derive
                the active mode from the persisted booleans (is_campaign_only
                takes precedence) so legacy bundles continue to render
                correctly. */}
            {(() => {
              const visibilityMode = formData.is_campaign_only
                ? 'campaign'
                : formData.is_global ? 'global' : 'company';
              const setMode = (mode) => setFormData(prev => ({
                ...prev,
                is_global: mode === 'global',
                is_campaign_only: mode === 'campaign',
                // Reset company list when switching away from company mode
                company_ids: mode === 'company' ? prev.company_ids : [],
              }));
              const cardCls = (active) =>
                `flex items-center gap-3 p-3 border rounded-lg cursor-pointer flex-1 ${
                  active ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                }`;
              const iconCls = (active) =>
                `p-2 rounded-full ${active ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`;
              return (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <label
                      className={cardCls(visibilityMode === 'global')}
                      onClick={() => setMode('global')}
                      data-testid="visibility-global"
                    >
                      <div className={iconCls(visibilityMode === 'global')}>
                        <Globe className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Global</p>
                        <p className="text-xs text-slate-500">Visible to all companies</p>
                      </div>
                    </label>

                    <label
                      className={cardCls(visibilityMode === 'company')}
                      onClick={() => setMode('company')}
                      data-testid="visibility-company"
                    >
                      <div className={iconCls(visibilityMode === 'company')}>
                        <Building2 className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Company-specific</p>
                        <p className="text-xs text-slate-500">Visible to selected companies</p>
                      </div>
                    </label>

                    <label
                      className={cardCls(visibilityMode === 'campaign')}
                      onClick={() => setMode('campaign')}
                      data-testid="visibility-campaign"
                    >
                      <div className={iconCls(visibilityMode === 'campaign')}>
                        <Megaphone className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Campaign-only</p>
                        <p className="text-xs text-slate-500">Only inside attached campaigns</p>
                      </div>
                    </label>
                  </div>

                  {visibilityMode === 'company' && (
                    <div className="space-y-2">
                      <Label>Select Companies *</Label>
                      <CompanyMultiSelect
                        selectedIds={formData.company_ids}
                        onChange={(ids) => setFormData(prev => ({ ...prev, company_ids: ids }))}
                        companies={companies}
                        onSearch={onSearchCompanies}
                      />
                      <p className="text-xs text-slate-500">
                        This bundle will only appear in Quick Order for users from these companies.
                      </p>
                    </div>
                  )}

                  {visibilityMode === 'campaign' && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                      This bundle is hidden from the normal Quick Order list. Attach it to a campaign in the
                      <strong> Campaigns </strong> panel — it will only be selectable via that campaign's public link.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="bundle-save-btn">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isNew ? 'Create Bundle' : 'Save Changes'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Cascade confirmation overlay. Higher z-index than the editor modal
          so it sits on top. 3-way choice: Cancel (back to editor),
          "Save bundle only" (no order changes), "Save & update X orders"
          (cascade flag). */}
      {cascadePrompt && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
          data-testid="cascade-confirm-overlay"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5 border-b">
              <h3 className="text-base font-semibold text-slate-900">
                Apply spec changes to existing orders?
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {cascadePrompt.affected_count} live order
                {cascadePrompt.affected_count === 1 ? '' : 's'} use this bundle and
                {cascadePrompt.affected_count === 1 ? ' is' : ' are'} eligible to
                receive the new spec values. Prices are not recalculated.
              </p>
            </div>
            <div className="p-5 space-y-2 max-h-64 overflow-y-auto">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Will be updated
              </p>
              <ul className="text-sm text-slate-700 space-y-1">
                {(cascadePrompt.affected_orders || []).slice(0, 8).map((o) => (
                  <li key={o.id} className="flex items-center justify-between">
                    <span>#{o.order_number} — {o.title || '—'}</span>
                    <Badge variant="secondary" className="text-[10px]">{o.status}</Badge>
                  </li>
                ))}
                {cascadePrompt.affected_orders?.length > 8 && (
                  <li className="text-xs text-slate-400">
                    …and {cascadePrompt.affected_orders.length - 8} more
                  </li>
                )}
              </ul>
              {cascadePrompt.excluded_count > 0 && (
                <p className="text-[11px] text-slate-400 pt-2 border-t mt-2">
                  {cascadePrompt.excluded_count} completed/shipped order
                  {cascadePrompt.excluded_count === 1 ? '' : 's'} will be skipped.
                </p>
              )}
            </div>
            <div className="p-4 border-t flex flex-wrap gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCascadePrompt(null)}
                disabled={saving}
                data-testid="cascade-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => performSave({ cascadeBundledOptions: false })}
                disabled={saving}
                data-testid="cascade-save-bundle-only"
              >
                Save bundle only
              </Button>
              <Button
                size="sm"
                onClick={() => performSave({ cascadeBundledOptions: true })}
                disabled={saving}
                data-testid="cascade-save-and-update"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                Save & update {cascadePrompt.affected_count} order
                {cascadePrompt.affected_count === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Bundle Card Component - consistent rendering everywhere
function BundleCard({ bundle, onEdit, onToggleActive, onDelete, onClone, rateCardItems = [], bomQuoteGateByProductId = {} }) {
  const tierConfig = TIER_CONFIG[bundle.tier] || TIER_CONFIG.standard;
  const TierIcon = tierConfig.icon;
  const companyNames = bundle.company_names || [];
  
  return (
    <Card className={`${!bundle.is_active ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge className={tierConfig.color}>
                {TierIcon && <TierIcon className="h-3 w-3 mr-1" />}
                {tierConfig.label}
              </Badge>
              {bundle.is_campaign_only ? (
                <Badge variant="outline" className="text-purple-600 border-purple-200 bg-purple-50">
                  <Megaphone className="h-3 w-3 mr-1" />
                  Campaign-only
                </Badge>
              ) : bundle.is_global ? (
                <Badge variant="outline" className="text-indigo-600 border-indigo-200 bg-indigo-50">
                  <Globe className="h-3 w-3 mr-1" />
                  Global
                </Badge>
              ) : companyNames.length > 0 ? (
                companyNames.length === 1 ? (
                  <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                    <Building2 className="h-3 w-3 mr-1" />
                    {companyNames[0]}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                    <Building2 className="h-3 w-3 mr-1" />
                    {companyNames.length} companies
                  </Badge>
                )
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                  <Building2 className="h-3 w-3 mr-1" />
                  No companies
                </Badge>
              )}
              {!bundle.is_active && (
                <Badge variant="outline" className="text-slate-500">
                  Inactive
                </Badge>
              )}
              {/* Compact Quote Required badge — rendered when the bundle
                  has a manual OR BOM-driven quote trigger. Uses the same
                  shared helper the editor + Sales pricing engine use, so
                  the overview badge can never disagree with the live
                  gate. */}
              {(() => {
                const q = computeBundleQuoteState({
                  baseSalesSku: bundle.base_sales_sku,
                  pricingStrategy: bundle.pricing_strategy,
                  rateCardItems,
                  bomQuoteGateByProductId,
                });
                if (q.state === 'none') return null;
                const srcLabel = q.state === 'both' ? 'Manual + BOM' : (q.state === 'manual' ? 'Manual' : 'BOM');
                const tipParts = [];
                if (q.manual) {
                  tipParts.push(`Manual override on: ${q.triggering_manual_skus.slice(0, 3).join(', ')}${q.triggering_manual_skus.length > 3 ? '…' : ''}`);
                }
                if (q.bom) {
                  const names = q.bom_components.slice(0, 3).map(c => c.procurement_item_name || c.supplier_sku).join(', ');
                  tipParts.push(`BOM items missing a fixed price: ${names}${q.bom_components.length > 3 ? '…' : ''}`);
                }
                return (
                  <Badge
                    variant="outline"
                    className="text-amber-700 border-amber-300 bg-amber-50"
                    title={tipParts.join(' · ')}
                    data-testid={`bundle-quote-required-badge-${bundle.id}`}
                  >
                    Quote Required · {srcLabel}
                  </Badge>
                );
              })()}
              <span className="text-xs text-slate-400">
                Order: {bundle.display_order}
              </span>
            </div>
            
            <h3 className="text-lg font-semibold text-slate-900">
              {bundle.title}
            </h3>
            {bundle.subtitle && (
              <p className="text-sm text-slate-500">{bundle.subtitle}</p>
            )}
            
            {/* Show company list if multiple */}
            {!bundle.is_global && companyNames.length > 1 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {companyNames.map((name, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {name}
                  </Badge>
                ))}
              </div>
            )}
            
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
              <span>
                <strong>SKU:</strong> {bundle.base_sales_sku || 'Not set'}
              </span>
              <span>
                <strong>Quantities:</strong> {bundle.allowed_quantity_choices?.join(', ')}
              </span>
              <span
                className="inline-flex items-center gap-1"
                title={
                  bundle.artwork_template_count == null
                    ? 'Bundle SKU could not be resolved — link a valid Sales Rate item to see template count.'
                    : bundle.artwork_template_count === 0
                      ? 'No Artwork Templates linked to this bundle\'s SKU yet.'
                      : `${bundle.artwork_template_count} Artwork Template${bundle.artwork_template_count === 1 ? '' : 's'} inherited from the Sales Rate item.`
                }
                data-testid={`bundle-artwork-template-count-${bundle.id}`}
              >
                <FileImage className="h-3.5 w-3.5 text-slate-400" />
                <strong>
                  {bundle.artwork_template_count == null ? '—' : bundle.artwork_template_count}
                </strong>
                <span>
                  Artwork Template{bundle.artwork_template_count === 1 ? '' : 's'}
                </span>
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggleActive(bundle)}
              title={bundle.is_active ? 'Deactivate' : 'Activate'}
            >
              {bundle.is_active ? (
                <Eye className="h-4 w-4 text-emerald-500" />
              ) : (
                <EyeOff className="h-4 w-4 text-slate-400" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(bundle)}
              data-testid={`edit-bundle-${bundle.id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onClone?.(bundle)}
              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
              title="Clone bundle"
              data-testid={`clone-bundle-${bundle.id}`}
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(bundle)}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main QuickBundlesManager component
export default function QuickBundlesManager({ bundles: initialBundles, rateCards, productFamily = 'vinyl' }) {
  const [bundles, setBundles] = useState(initialBundles || []);
  const [rateCardItems, setRateCardItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(!initialBundles);
  const [editingBundle, setEditingBundle] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Per-BOM quote-gate cache (same mechanism as SalesRateCardTab).
  // Keyed by bom_product_id → { quote_required, count, components }. We
  // only call the shared ``POST /api/bom/quote-check`` endpoint once per
  // unique BOM across the loaded rate-card items, so the editor preview,
  // the overview badge and the Sales pricing engine can never disagree.
  const [bomQuoteGateByProductId, setBomQuoteGateByProductId] = useState({});

  useEffect(() => {
    const uniqueBomIds = Array.from(
      new Set(
        (rateCardItems || [])
          .map((it) => (it && it.generate_purchase_line !== false ? it.bom_product_id : null))
          .filter(Boolean)
      )
    );
    if (uniqueBomIds.length === 0) {
      setBomQuoteGateByProductId({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          uniqueBomIds.map(async (pid) => {
            try {
              const res = await fetch(`${API_URL}/api/bom/quote-check`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ product_id: pid }),
              });
              if (!res.ok) return [pid, { quote_required: false, count: 0, components: [] }];
              const body = await res.json();
              return [pid, {
                quote_required: !!body.quote_required,
                count: body.count || 0,
                components: body.components || [],
              }];
            } catch {
              return [pid, { quote_required: false, count: 0, components: [] }];
            }
          })
        );
        if (cancelled) return;
        const map = {};
        for (const [pid, gate] of results) map[pid] = gate;
        setBomQuoteGateByProductId(map);
      } catch {
        if (!cancelled) setBomQuoteGateByProductId({});
      }
    })();
    return () => { cancelled = true; };
  }, [rateCardItems]);

  // Filter/search state — kept in-memory only (no URL params). Bundles
  // are cheap to filter client-side since a single family typically has
  // <200 rows, and this avoids extra round-trips when admins clear the
  // filter after finding what they were looking for.
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all'); // 'all' | 'global' | <company_id>

  // Filtered view of bundles — matches searchTerm against title,
  // subtitle and SKU, and applies the company scope selector. Kept in
  // a `useMemo` so it doesn't rerun on unrelated state changes, and
  // declared up here (not next to the render block) to obey the
  // rules-of-hooks — the render block has an early `if (loading)` bail
  // that would otherwise short-circuit this hook on the first paint.
  const filteredBundles = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return bundles.filter(b => {
      if (companyFilter === 'global') {
        if (!b.is_global) return false;
      } else if (companyFilter !== 'all') {
        // Specific company id — bundle must be non-global AND list this
        // company. Globals stay in the "All" view (admins can flip to
        // "Global bundles only" to see them alone).
        if (b.is_global) return false;
        if (!(b.company_ids || []).includes(companyFilter)) return false;
      }
      if (q) {
        const hay = [
          b.title || '',
          b.subtitle || '',
          b.base_sales_sku || '',
        ].join('\n').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [bundles, searchTerm, companyFilter]);

  // Fetch bundles
  const fetchBundles = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/product-config/admin/bundles?product_family=${encodeURIComponent(productFamily)}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setBundles(data);
      }
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
      toast.error('Failed to load bundles');
    }
  }, [productFamily]);

  // Fetch rate card items from ALL rate cards in current family
  const fetchRateCardItems = useCallback(async () => {
    try {
      const cardsRes = await fetch(
        `${API_URL}/api/sales/rate-cards?product_family=${encodeURIComponent(productFamily)}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (!cardsRes.ok) return;
      
      const cards = await cardsRes.json();
      if (cards.length === 0) return;

      // Fetch items from ALL rate cards and combine them
      const allItems = [];
      const seenSkus = new Set();
      
      for (const card of cards) {
        try {
          const itemsRes = await fetch(`${API_URL}/api/sales/rate-cards/${card.id}/items`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
          });
          if (itemsRes.ok) {
            const items = await itemsRes.json();
            // Deduplicate by sales_sku
            for (const item of items) {
              if (!seenSkus.has(item.sales_sku)) {
                seenSkus.add(item.sales_sku);
                allItems.push(item);
              }
            }
          }
        } catch (err) {
          console.error(`Failed to fetch items from card ${card.id}:`, err);
        }
      }
      
      setRateCardItems(allItems);
    } catch (error) {
      console.error('Failed to fetch rate card items:', error);
    }
  }, [productFamily]);

  // Fetch companies
  const fetchCompanies = useCallback(async (search = '') => {
    try {
      const url = search 
        ? `${API_URL}/api/product-config/admin/companies?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/product-config/admin/companies`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        !initialBundles && fetchBundles(),
        fetchRateCardItems(),
        fetchCompanies()
      ]);
      setLoading(false);
    };
    init();
  }, [fetchBundles, fetchRateCardItems, fetchCompanies, initialBundles]);

  // Update bundles when initialBundles prop changes
  useEffect(() => {
    if (initialBundles) {
      setBundles(initialBundles);
    }
  }, [initialBundles]);

  const handleCreateBundle = async (formData) => {
    const res = await fetch(`${API_URL}/api/product-config/admin/bundles`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      // Phase 2: attach the parent's active product_family
      body: JSON.stringify({ product_family: productFamily, ...formData })
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to create bundle');
    }

    toast.success('Bundle created successfully');
    fetchBundles();
  };

  const handleUpdateBundle = async (formData, { cascadeBundledOptions = false } = {}) => {
    const qs = cascadeBundledOptions ? '?cascade_bundled_options=true' : '';
    const res = await fetch(`${API_URL}/api/product-config/admin/bundles/${editingBundle.id}${qs}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to update bundle');
    }

    toast.success(
      cascadeBundledOptions
        ? 'Bundle updated — specs cascaded to existing orders'
        : 'Bundle updated successfully'
    );
    setEditingBundle(null);
    fetchBundles();
  };

  const handleToggleActive = async (bundle) => {
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/bundles/${bundle.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !bundle.is_active })
      });

      if (res.ok) {
        toast.success(`Bundle ${bundle.is_active ? 'deactivated' : 'activated'}`);
        fetchBundles();
      }
    } catch (error) {
      toast.error('Failed to update bundle status');
    }
  };

  const handleDeleteBundle = async (bundle) => {
    if (!window.confirm(`Delete "${bundle.title}"? This cannot be undone.`)) return;

    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/bundles/${bundle.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (res.ok) {
        toast.success('Bundle deleted');
        fetchBundles();
      }
    } catch (error) {
      toast.error('Failed to delete bundle');
    }
  };

  const handleCloneBundle = async (bundle) => {
    // Server-side deep clone; immediately open the copy in Edit mode so
    // the admin can tweak the copy before it goes live. No confirm — the
    // clone is inactive/visible only if the source was, and can be
    // deleted from the same list on the same page.
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/bundles/${bundle.id}/clone`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to clone bundle');
      }
      const cloned = await res.json();
      toast.success('Bundle cloned — edit and save to publish');
      await fetchBundles();
      setEditingBundle(cloned);
    } catch (error) {
      toast.error(error.message || 'Failed to clone bundle');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Manage pre-configured packages for Quick Order mode
        </p>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-bundle-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Bundle
        </Button>
      </div>

      {/* Filter bar — visible whenever there are ≥5 bundles OR any
          filter is already active. Below the threshold the list is
          short enough that filters just add visual noise. */}
      {(bundles.length >= 5 || searchTerm || companyFilter !== 'all') && (
        <div className="flex flex-col md:flex-row gap-3 md:items-center" data-testid="bundle-filter-bar">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, subtitle or SKU…"
              className="pl-9"
              data-testid="bundle-search-input"
            />
          </div>
          <div className="w-full md:w-72">
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger data-testid="bundle-company-filter">
                <SelectValue placeholder="Filter by company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bundles</SelectItem>
                <SelectItem value="global">Global bundles only</SelectItem>
                {companies.length > 0 && (
                  <>
                    <div className="my-1 h-px bg-slate-100" />
                    {companies
                      .slice()
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          {(searchTerm || companyFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearchTerm(''); setCompanyFilter('all'); }}
              className="text-xs"
              data-testid="bundle-filter-reset"
            >
              Reset
            </Button>
          )}
          <span className="text-xs text-slate-500 whitespace-nowrap" data-testid="bundle-filter-count">
            {filteredBundles.length} of {bundles.length}
          </span>
        </div>
      )}

      {/* Bundles List */}
      {bundles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No bundles yet</h3>
            <p className="text-sm text-slate-500 mb-4">
              Create your first Quick Order bundle to get started
            </p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Bundle
            </Button>
          </CardContent>
        </Card>
      ) : filteredBundles.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-slate-700 mb-1">No bundles match</h3>
            <p className="text-sm text-slate-500">
              Try a different search term or clear the company filter.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredBundles.map(bundle => (
            <BundleCard
              key={bundle.id}
              bundle={bundle}
              onEdit={setEditingBundle}
              onToggleActive={handleToggleActive}
              onDelete={handleDeleteBundle}
              onClone={handleCloneBundle}
              rateCardItems={rateCardItems}
              bomQuoteGateByProductId={bomQuoteGateByProductId}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <BundleEditorModal
          bundle={null}
          rateCardItems={rateCardItems}
          companies={companies}
          activeFamily={productFamily}
          bomQuoteGateByProductId={bomQuoteGateByProductId}
          onSave={handleCreateBundle}
          onClose={() => setShowCreateModal(false)}
          onSearchCompanies={fetchCompanies}
        />
      )}

      {/* Edit Modal */}
      {editingBundle && (
        <BundleEditorModal
          bundle={editingBundle}
          rateCardItems={rateCardItems}
          companies={companies}
          activeFamily={productFamily}
          bomQuoteGateByProductId={bomQuoteGateByProductId}
          onSave={handleUpdateBundle}
          onClose={() => setEditingBundle(null)}
          onSearchCompanies={fetchCompanies}
        />
      )}
    </div>
  );
}
